import { useState, useEffect } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { Link } from "wouter";
import { Check, Sparkles, ArrowLeft, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRICING_TIERS, PRO_FEATURES } from "@workspace/pricing";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { PageMeta } from "@/components/PageMeta";

const NAVY = "#10325b";
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function UpgradePage() {
  usePageTitle("Upgrade to Pro");
  const [annual, setAnnual] = useState(true);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const proTier = PRICING_TIERS.find((t) => t.id === "pro")!;

  const [liveMonthly, setLiveMonthly] = useState<number | null>(null);
  const [liveAnnualPerMonth, setLiveAnnualPerMonth] = useState<number | null>(null);

  useEffect(() => {
    fetch(`${BASE}/api/billing/prices`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => {
        const monthly = data?.monthly?.amount ?? null;
        const annualPerMonth =
          data?.annual?.perMonth != null
            ? data.annual.perMonth
            : data?.annual?.amount != null
            ? Math.round((data.annual.amount / 12) * 100) / 100
            : null;
        if (monthly != null) setLiveMonthly(monthly);
        if (annualPerMonth != null) setLiveAnnualPerMonth(annualPerMonth);
      })
      .catch(() => {});
  }, []);

  const pricesReady = liveMonthly !== null && liveAnnualPerMonth !== null;
  const displayPrice = pricesReady ? (annual ? liveAnnualPerMonth : liveMonthly) : null;

  const savingsPct =
    pricesReady && liveMonthly! > 0
      ? Math.round(((liveMonthly! - liveAnnualPerMonth!) / liveMonthly!) * 100)
      : null;

  const handleUpgrade = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/billing/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interval: annual ? "annual" : "monthly", rdt_cid: localStorage.getItem("rdt_cid") || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast({
        title: "Checkout failed",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  if (user?.plan === "pro") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
          <Sparkles className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-2xl font-bold">You're already on Pro</h1>
        <p className="text-muted-foreground max-w-sm">You have full access to all Pro features. Enjoy building your business!</p>
        <div className="flex gap-3">
          <Link href="/">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <Link href="/settings">
            <Button variant="outline">Manage Billing</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <PageMeta
        title="Upgrade to Pro — QuoTrak"
        description="Unlock email sending, recurring invoices, cost tracking, custom branding, and more with QuoTrak Pro."
        path="/upgrade"
        noindex
      />
      <div className="mb-8">
        <Link href="/">
          <button className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: NAVY }}>Upgrade to Pro</h1>
        </div>
        <p className="text-muted-foreground">
          Unlock everything QuoTrak has to offer — run a faster, more professional business.
        </p>
      </div>

      <div className="rounded-2xl border-2 border-primary bg-card shadow-lg overflow-hidden mb-8">
        <div className="px-8 py-6 border-b border-border bg-primary/5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg font-bold" style={{ color: NAVY }}>Pro Plan</span>
                <span className="text-xs font-medium bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Most Popular</span>
              </div>
              <p className="text-sm text-muted-foreground">{proTier.tagline}</p>
            </div>
            <div className="text-right">
              <div className="flex items-baseline gap-1">
                {displayPrice !== null ? (
                  <>
                    <span className="text-4xl font-bold" style={{ color: NAVY }}>${displayPrice}</span>
                    <span className="text-muted-foreground">/mo</span>
                  </>
                ) : (
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">{annual ? "billed annually" : "billed monthly"}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4" role="group" aria-label="Billing interval">
            <button
              onClick={() => setAnnual(false)}
              aria-pressed={!annual}
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${!annual ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              aria-pressed={annual}
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${annual ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
            >
              Annual
            </button>
            {annual && savingsPct !== null && (
              <span className="text-xs text-green-600 font-medium">Save {savingsPct}%</span>
            )}
          </div>
        </div>

        <div className="px-8 py-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">Everything in Free, plus:</p>
          <ul className="space-y-3 mb-6">
            {proTier.features.map((f) => (
              <li key={f} className="flex items-center gap-2.5 text-sm">
                <Check className="w-4 h-4 shrink-0 text-primary" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <Button
            className="w-full"
            size="lg"
            onClick={handleUpgrade}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Redirecting to checkout…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {displayPrice !== null ? `Upgrade to Pro — $${displayPrice}/mo` : "Upgrade to Pro"}
              </>
            )}
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-3">
            Secure checkout powered by Stripe. Cancel anytime.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">What's unlocked with Pro</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.values(PRO_FEATURES).map((label) => (
            <div key={label} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
              {label}
            </div>
          ))}
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-8">
        Questions?{" "}
        <Link href="/support">
          <span className="underline cursor-pointer hover:text-foreground">Contact support</span>
        </Link>
      </p>
    </div>
  );
}
